import moment from "moment-timezone";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

const getStartDate = (user, plan) => {
  switch (plan.tierBy) {
    case "hire-date": {
      if (!user.hiringDate) {
        return null;
      }
      return moment(user.hiringDate).format("YYYY-MM-DD");
    }
    case "seniority": {
      if (!user.dateOfSeniority) {
        return null;
      }
      return moment(user.dateOfSeniority).format("YYYY-MM-DD");
    }
    default: {
      return null;
    }
  }
}

export const useDurationValidations = ({ user, timeOffPlans }) => {
  const { t } = useTranslation();

  const isBiggerThanZero = useCallback((value) => {
    if (value <= 0) {
      return t('duration-bigger-than-zero');
    }
    return null;
  }, [t]);

  const getTimeOffPlan = useCallback((timeSheet, timeOffPlans) => {
    return timeOffPlans?.find((plan) => {
      return (
        plan.startDate <= timeSheet.date
        && plan.endDate >= timeSheet.date
        && plan.plan.payCode === timeSheet.payCode?.id
        // the probation pay code should also be included here
      );
    });
  }, []);

  const hasSurpassedTimeOffMaxRequestedHours = useCallback((_, timeSheet) => {
    const userTimeOffPlan = getTimeOffPlan(timeSheet, timeOffPlans);
    if (!userTimeOffPlan) {
      return null;
    }
    const plan = userTimeOffPlan.plan;
    const startDate = getStartDate(user, plan);
    if (!startDate) {
      return null;
    }

    const years = moment().diff(moment(startDate, 'YYYY-MM-DD'), 'years');
    const tier = plan.tiers.findIndex((tier) => {
      return (
        (!tier.startDate || tier.startDate <= years)
        && (!tier.endDate || tier.endDate >= years)
      );
    });
    if (tier === undefined) {
      return null;
    }

    const maxRequestedHours = Number(plan.maxRequestedHours?.[tier]?.value);
    if (!maxRequestedHours) {
      return null;
    }
    const duration = moment.duration(timeSheet.duration, 'seconds').asHours();
    if (duration > maxRequestedHours) {
      return t('max-requested-hours-exceeded');
    }

    return null;
  }, [timeOffPlans, user, t]);

  const hasViolatedTimeOffMinRequestedHours = useCallback((value, timeSheet) => {
    const userTimeOffPlan = getTimeOffPlan(timeSheet, timeOffPlans);
    if (!userTimeOffPlan) {
      return null;
    }

    const plan = userTimeOffPlan.plan;
    const minRequestedHours = Number(plan.minRequestedHours);
    if (!minRequestedHours) {
      return null;
    }

    const duration = moment.duration(value, 'seconds').asHours();
    if (duration % minRequestedHours !== 0) {
      return t('duration-not-multiple-of-min-requested-hours');
    }

    return null;
  }, [timeOffPlans, user, t]);

  return useMemo(() => {
    return [
      isBiggerThanZero,
      hasSurpassedTimeOffMaxRequestedHours,
      hasViolatedTimeOffMinRequestedHours,
    ];
  }, [hasSurpassedTimeOffMaxRequestedHours]);
}
